/**
 * <timeline-switcher> — builds the rail of dates from each entry's label and shows
 * one entry at a time.
 *
 * It is a set of tabs with a line through it, so it is marked up as one: a tablist
 * of buttons, each owning a tabpanel, with arrow keys moving along the rail.
 *
 * The line fills up to the chosen date and every date before it is marked as
 * passed, so the rail keeps saying where in the story the reader is — the thing a
 * row of plain tabs can't do.
 */
(() => {
  let uid = 0;

  class TimelineSwitcher extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-timeline-rail]');
      this.scroller = this.querySelector('[data-timeline-scroller]');
      // Direct entries only: a timeline nested inside an entry must not donate
      // its dates to this rail.
      this.panels = [...this.querySelectorAll('[data-timeline-panel]')].filter(
        (panel) => panel.closest('timeline-switcher') === this
      );
      if (!this.rail || !this.panels.length) return;

      const id = `timeline-${(uid += 1)}`;

      // Rebuilt from scratch: the editor re-runs this after every change.
      this.rail.querySelectorAll('.timeline__node').forEach((node) => node.remove());

      this.nodes = this.panels.map((panel, index) => {
        const node = document.createElement('button');
        node.type = 'button';
        node.className = 'timeline__node';
        node.id = `${id}-node-${index}`;
        node.setAttribute('role', 'tab');
        node.setAttribute('aria-controls', `${id}-panel-${index}`);

        const dot = document.createElement('span');
        dot.className = 'timeline__dot';
        dot.setAttribute('aria-hidden', 'true');

        const label = document.createElement('span');
        label.className = 'timeline__label';
        label.textContent = panel.dataset.label || String(index + 1);

        node.append(dot, label);
        node.addEventListener('click', () => this.#activate(index));
        node.addEventListener('keydown', (event) => this.#onKey(event, index));
        this.rail.appendChild(node);

        panel.id = `${id}-panel-${index}`;
        panel.setAttribute('aria-labelledby', node.id);
        return node;
      });

      this.style.setProperty('--timeline-count', this.nodes.length);

      // Nothing moves before the reader has touched it.
      this.#activate(0, { scroll: false, focus: false });
      this.classList.add('is-ready');

      // Selecting an entry in the theme editor shows it, so the merchant is never
      // editing an entry they can't see.
      this.onEditorSelect = (event) => {
        const index = this.panels.findIndex((panel) => panel.contains(event.target));
        if (index > -1) this.#activate(index, { focus: false });
      };
      document.addEventListener('shopify:block:select', this.onEditorSelect);
    }

    disconnectedCallback() {
      if (this.onEditorSelect) {
        document.removeEventListener('shopify:block:select', this.onEditorSelect);
      }
    }

    #activate(index, { scroll = true, focus = false } = {}) {
      const last = this.nodes.length - 1;
      this.active = index;

      this.nodes.forEach((node, i) => {
        const selected = i === index;
        node.setAttribute('aria-selected', selected ? 'true' : 'false');
        // Roving tabindex: one stop for the whole rail, arrows move within it.
        node.tabIndex = selected ? 0 : -1;
        node.classList.toggle('is-past', i < index);
      });

      this.panels.forEach((panel, i) => {
        const selected = i === index;
        panel.classList.toggle('is-active', selected);
        panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });

      this.style.setProperty('--timeline-progress', last > 0 ? index / last : 0);

      if (focus) this.nodes[index].focus({ preventScroll: true });
      if (scroll) this.#reveal(index);
    }

    #onKey(event, index) {
      const last = this.nodes.length - 1;
      const moves = {
        ArrowRight: Math.min(index + 1, last),
        ArrowLeft: Math.max(index - 1, 0),
        Home: 0,
        End: last,
      };
      if (!(event.key in moves)) return;
      event.preventDefault();
      this.#activate(moves[event.key], { focus: true });
    }

    /*
      On a rail too long for its column, keeps the chosen date and the one after it
      in view — the same walk-along the tab strip does, so the next date is always
      one click away.
    */
    #reveal(index) {
      const scroller = this.scroller;
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

      const node = this.nodes[index];
      const next = this.nodes[index + 1] || node;
      const prev = this.nodes[index - 1] || node;
      const pad = 16;

      let target = scroller.scrollLeft;
      const nextRight = next.offsetLeft + next.offsetWidth;
      if (nextRight > target + scroller.clientWidth) target = nextRight - scroller.clientWidth + pad;
      if (prev.offsetLeft < target) target = prev.offsetLeft - pad;

      const max = scroller.scrollWidth - scroller.clientWidth;
      scroller.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
    }
  }

  if (!customElements.get('timeline-switcher')) {
    customElements.define('timeline-switcher', TimelineSwitcher);
  }
})();
